import { useState, useMemo } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import {
  Box,
  Typography,
  Tooltip,
  Paper,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { DeliveryUnit } from "../../../data/HR/DeliveryUnit";
import { getPublicHolidays } from "../../../data/HR/PublicHolidays";
import { parseDateOnlyToLocal } from "../../../helpers/parseDateOnly";
import {
  normalizeTimeOffDayPortion,
  timeOffDayPortionLabel,
} from "../../../enums/HR/TimeOffDayPortionEnum";

const departments = [
  "All",
  "Dispatch",
  "Brokerage",
  "Safety",
  "Accounting",
  "Data Analytics Department",
  "HR",
  "Permits",
  "IT",
  "Management",
  "Operations",
  "Fleet",
  "Truck Sales",
  "Insurance",
  "Intermodal",
];

const generateDateRange = (start: Date, end: Date) => {
  const dateArray = [];
  let currentDate = new Date(start);
  while (currentDate <= end) {
    dateArray.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dateArray;
};

const isSameDay = (d1: Date, d2: Date) =>
  d1.getDate() === d2.getDate() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getFullYear() === d2.getFullYear();

interface Availability {
  id?: string;
  policy: string;
  startDate: Date;
  endDate: Date;
  isHalfDay: boolean;
  dayPortion: unknown;
  status?: string;
}

interface PersonData {
  firstName: string;
  lastName: string;
  availabilities: Availability[];
  department?: string;
  deliveryUnit?: string;
}

const groupByPerson = (data: any): Record<string, PersonData> => {
  return data.reduce((acc: any, record: any) => {
    const personKey = `${record.firstName}-${record.lastName}`;
    if (!acc[personKey]) {
      acc[personKey] = {
        firstName: record.firstName,
        lastName: record.lastName,
        availabilities: [],
        department: record.department,
        deliveryUnit: record.deliveryUnit, // <-- add deliveryUnit
      };
    }
    acc[personKey].availabilities.push({
      id: record.id,
      policy: record.policy,
      startDate: new Date(record.startDate),
      endDate: new Date(record.endDate),
      isHalfDay: record.isHalfDay,
      dayPortion: record.dayPortion,
      status: record.status?.toLowerCase(),
    });
    return acc;
  }, {});
};

const fetchPendingRequests = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/getAllTimeOffs`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { pendingOnly: true },
  });
  return data;
};

const fetchPeopleAvailability = async (
  token: string | null | undefined,
  startDate: Date,
  endDate: Date,
) => {
  const url = `${apiVariable}/employeesAvailability?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
  const headers = { Authorization: `Bearer ${token}` };
  const { data } = await axios.get(url, { headers });
  return data;
};

const getDayDifference = (startDate: Date, endDate: Date) => {
  const start = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
  );
  const diffTime = end.getTime() - start.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.round(diffTime / oneDay);
};

const AvailabilityTimeline = () => {
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState<string>("All");
  const [selectedDeliveryUnit, setSelectedDeliveryUnit] = useState<string>("");
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const dayWidth = 49;

  const getMonthRange = (month: Date) => {
    const startOfMonth = new Date(
      month.getFullYear(),
      month.getMonth(),
      1,
      0,
      0,
      0,
      0,
    );
    const endOfMonth = new Date(
      month.getFullYear(),
      month.getMonth() + 1,
      0,
      23,
      59,
      59,
      999,
    );
    return { startOfMonth, endOfMonth };
  };
  const { startOfMonth, endOfMonth } = getMonthRange(currentMonth);

  const allBankHolidays = useMemo(() => {
    const currentYear = startOfMonth.getFullYear();
    const country =
      selectedDeliveryUnit === "US"
        ? "USA"
        : selectedDeliveryUnit === "MD"
          ? "Moldova"
          : undefined;
    return getPublicHolidays(currentYear, country).map((h) => ({
      date: parseDateOnlyToLocal(h.date),
      name: h.name,
      country: h.country,
    }));
  }, [startOfMonth, selectedDeliveryUnit]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["peopleAvailability", currentMonth],
    queryFn: () => fetchPeopleAvailability(token, startOfMonth, endOfMonth),
    enabled: !!token,
  });

  const { data: pendingData = [], isLoading: isPendingLoading } = useQuery({
    queryKey: ["availabilityPendingRequests"],
    queryFn: () => fetchPendingRequests(token),
    enabled: !!token && !!role?.includes("HR"),
  });

  if (isLoading || isPendingLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  if (isError)
    return (
      <Typography color="error" align="center">
        Error: {error.message}
      </Typography>
    );

  const dateRange = generateDateRange(startOfMonth, endOfMonth);
  const today = new Date();
  const normalizedPendingData = pendingData.map((request: any) => ({
    ...request,
    firstName: request.firstName || request.employee?.firstName,
    lastName: request.lastName || request.employee?.lastName,
    department: request.department || request.employee?.department,
    deliveryUnit: request.deliveryUnit || request.employee?.deliveryUnit,
    status: "pending",
  }));
  // The availability endpoint remains the source of approved absences. Any
  // pending rows it may return are intentionally excluded and replaced with
  // the permission-gated request data below, so they can never look approved.
  const approvedData = (data || []).filter(
    (entry: any) => entry.status?.toLowerCase() !== "pending",
  );
  const groupedData = groupByPerson([
    ...approvedData,
    ...normalizedPendingData,
  ]);

  const filteredData = Object.entries(groupedData).reduce(
    (acc: Record<string, PersonData>, [personKey, personData]) => {
      const filteredAvailabilities = personData.availabilities.filter(
        (availability) =>
          availability.startDate <= endOfMonth &&
          availability.endDate >= startOfMonth,
      );

      const matchesDepartment =
        selectedDepartment === "All" ||
        personData.department === selectedDepartment;

      const matchesDeliveryUnit =
        !selectedDeliveryUnit ||
        (personData.deliveryUnit &&
          personData.deliveryUnit.toLowerCase() ===
            selectedDeliveryUnit.toLowerCase());

      if (
        matchesDepartment &&
        matchesDeliveryUnit &&
        filteredAvailabilities.length > 0
      ) {
        acc[personKey] = {
          ...personData,
          availabilities: filteredAvailabilities,
        };
      }

      return acc;
    },
    {},
  );

  const getRandomColor = (policy: string) => {
    const hash = policy
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0, width: "100%" }}>
      <Box
        display="grid"
        gap={2}
        mb={2}
        sx={{
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, 200px)" },
          justifyContent: "center",
        }}
      >
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel>Department</InputLabel>
          <Select
            value={selectedDepartment}
            onChange={(e) => {
              setSelectedDepartment(e.target.value);
              setSelectedDeliveryUnit("");
            }}
            label="Department"
          >
            {departments.map((department) => (
              <MenuItem key={department} value={department}>
                {department}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id="availability-delivery-unit-label">
            Delivery Unit
          </InputLabel>
          <Select
            labelId="availability-delivery-unit-label"
            value={selectedDeliveryUnit}
            onChange={(e) => setSelectedDeliveryUnit(e.target.value)}
            label="Delivery Unit"
          >
            {DeliveryUnit.map((unit) => (
              <MenuItem key={unit.value} value={unit.value}>
                {unit.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Paper
        elevation={3}
        sx={{ p: { xs: 1, sm: 3 }, minWidth: 0, maxWidth: "100%" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <IconButton
            onClick={() =>
              setCurrentMonth(
                new Date(
                  currentMonth.getFullYear(),
                  currentMonth.getMonth() - 1,
                  1,
                ),
              )
            }
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            {currentMonth.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </Typography>
          <IconButton
            onClick={() =>
              setCurrentMonth(
                new Date(
                  currentMonth.getFullYear(),
                  currentMonth.getMonth() + 1,
                  1,
                ),
              )
            }
          >
            <ArrowForward />
          </IconButton>
        </Box>

        <Box
          role="region"
          aria-label="Employee availability calendar"
          tabIndex={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            mt: 2,
            overflow: "auto",
            maxHeight: 600,
            width: "100%",
            overscrollBehaviorX: "contain",
            isolation: "isolate",
          }}
        >
          <Box
            sx={{
              display: "flex",
              minWidth: {
                xs: `${140 + dateRange.length * dayWidth}px`,
                sm: `${200 + dateRange.length * dayWidth}px`,
              },
              width: {
                xs: `${140 + dateRange.length * dayWidth}px`,
                sm: `${200 + dateRange.length * dayWidth}px`,
              },
              position: "sticky",
              top: 0,
              zIndex: 3,
              backgroundColor: "background.paper",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Box
              sx={{
                width: { xs: 140, sm: 200 },
                overflow: "hidden",
                px: 1,
                textAlign: "left",
                flexShrink: 0,
                position: "sticky",
                left: 0,
                top: 0,
                zIndex: 4,
                backgroundColor: "background.paper",
                display: "flex",
                alignItems: "center",
                borderRight: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Employees
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                width: `${dateRange.length * dayWidth}px`,
              }}
            >
              {dateRange.map((date) => {
                const isToday = isSameDay(date, today);
                const holiday = allBankHolidays.find((h) =>
                  isSameDay(h.date, date),
                );

                let backgroundColor = "transparent";
                if (isToday && holiday) backgroundColor = "#FFA500";
                else if (isToday) backgroundColor = "#D3D3D3";
                else if (holiday) backgroundColor = "#FFCCCC";

                return (
                  <Tooltip
                    key={date.toISOString()}
                    title={
                      holiday ? `${holiday.name} (${holiday.country})` : ""
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        width: `${dayWidth}px`,
                        flexShrink: 0,
                        textAlign: "center",
                        borderRight: "1px solid #e0e0e0",
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor,
                      }}
                    >
                      <Typography variant="caption" display="block">
                        {date.toLocaleDateString("en-US", {
                          weekday: "short",
                        })}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {date.toLocaleDateString("en-US", { day: "numeric" })}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          {Object.entries(filteredData).map(([personKey, personData]) => (
            <Box
              key={personKey}
              sx={{
                display: "flex",
                alignItems: "center",
                width: {
                  xs: `${140 + dateRange.length * dayWidth}px`,
                  sm: `${200 + dateRange.length * dayWidth}px`,
                },
                minWidth: {
                  xs: `${140 + dateRange.length * dayWidth}px`,
                  sm: `${200 + dateRange.length * dayWidth}px`,
                },
                borderBottom: "1px solid #e0e0e0",
                height: "44px",
              }}
            >
              <Box
                sx={{
                  width: { xs: 140, sm: 200 },
                  overflow: "hidden",
                  px: 1,
                  flexShrink: 0,
                  position: "sticky",
                  left: 0,
                  backgroundColor: "background.paper",
                  zIndex: 1,
                  borderRight: "1px solid #e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  noWrap
                  title={`${personData.firstName} ${personData.lastName}`}
                >
                  {`${personData.firstName} ${personData.lastName}`}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: `${dateRange.length * dayWidth}px`,
                  flexShrink: 0,
                  height: "44px",
                }}
              >
                {personData.availabilities.map(
                  (availability: any, index: number) => {
                    const adjustedStartDate =
                      availability.startDate < startOfMonth
                        ? startOfMonth
                        : availability.startDate;
                    const adjustedEndDate =
                      availability.endDate > endOfMonth
                        ? endOfMonth
                        : availability.endDate;

                    const startDayIndex = getDayDifference(
                      startOfMonth,
                      adjustedStartDate,
                    );
                    const durationDays =
                      getDayDifference(adjustedStartDate, adjustedEndDate) + 1;

                    const portion = normalizeTimeOffDayPortion(
                      availability.dayPortion,
                      availability.isHalfDay,
                    );
                    const isHalfDay = portion !== "fullDay";
                    const left =
                      startDayIndex * dayWidth +
                      2 +
                      (portion === "secondHalf" ? dayWidth / 2 : 0);
                    const width = isHalfDay
                      ? dayWidth / 2 - 2
                      : durationDays * dayWidth - 4;
                    const isPending = availability.status === "pending";

                    return (
                      <Tooltip
                        key={index}
                        title={`${isPending ? "Pending request — " : ""}${camelCaseToSentenceCase(
                          availability.policy,
                        )} (${timeOffDayPortionLabel(
                          availability.dayPortion,
                          availability.isHalfDay,
                        )}): ${personData.firstName} ${personData.lastName}`}
                        arrow
                      >
                        <Box
                          role={isPending ? "link" : undefined}
                          tabIndex={isPending ? 0 : undefined}
                          aria-label={
                            isPending
                              ? `Review pending ${availability.policy} request for ${personData.firstName} ${personData.lastName}`
                              : undefined
                          }
                          onClick={() =>
                            isPending &&
                            navigate(
                              `/hr/time-off-requests?requestId=${availability.id}`,
                            )
                          }
                          onKeyDown={(event) => {
                            if (
                              isPending &&
                              (event.key === "Enter" || event.key === " ")
                            ) {
                              event.preventDefault();
                              navigate(
                                `/hr/time-off-requests?requestId=${availability.id}`,
                              );
                            }
                          }}
                          sx={{
                            position: "absolute",
                            left: `${left}px`,
                            width: `${width}px`,
                            top: "3px",
                            bottom: "3px",
                            backgroundColor: isPending
                              ? "rgba(237, 108, 2, 0.14)"
                              : getRandomColor(availability.policy),
                            backgroundImage: isPending
                              ? "repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(237,108,2,.18) 5px, rgba(237,108,2,.18) 10px)"
                              : undefined,
                            borderRadius: isPending ? 0 : "4px",
                            clipPath: isPending
                              ? "polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%)"
                              : undefined,
                            border: isPending
                              ? "2px dashed #ed6c02"
                              : isHalfDay
                                ? "1px solid rgba(0,0,0,0.35)"
                                : undefined,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: isPending ? "pointer" : "default",
                          }}
                        />
                      </Tooltip>
                    );
                  },
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default AvailabilityTimeline;
